import React, { useState, useEffect, useCallback } from "react";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const MAX_LENGTH = 2499;

const TextEditorQuestion = ({ editorValue, editorStateValue }) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  // Listen for the initial data load from the API and convert HTML to DraftJS
  useEffect(() => {
    if (editorValue) {
      const blocksFromHtml = htmlToDraft(editorValue);
      if (blocksFromHtml) {
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
        setEditorState(EditorState.createWithContent(contentState));
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
    // We only want this to run when the parent passes a NEW editorValue (API load)
  }, [editorValue]); 

  // Handle user typing and send HTML back to parent
  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
    if (editorStateValue) {
      editorStateValue(draftToHtml(convertToRaw(newEditorState.getCurrentContent())));
    }
  };

  // Character limit logic
  const getLengthOfSelectedText = useCallback(() => {
    const currentSelection = editorState.getSelection();
    const isCollapsed = currentSelection.isCollapsed();
    let length = 0;

    if (!isCollapsed) {
      const currentContent = editorState.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      const startBlock = currentContent.getBlockForKey(startKey);
      const isStartAndEndBlockAreTheSame = startKey === endKey;
      const startSelectedTextLength = startBlock.getLength() - currentSelection.getStartOffset();
      const endSelectedTextLength = currentSelection.getEndOffset();
      const keyAfterEnd = currentContent.getKeyAfter(endKey);

      if (isStartAndEndBlockAreTheSame) {
        length += currentSelection.getEndOffset() - currentSelection.getStartOffset();
      } else {
        let currentKey = startKey;
        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1;
          } else if (currentKey === endKey) {
            length += endSelectedTextLength;
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1;
          }
          currentKey = currentContent.getKeyAfter(currentKey);
        }
      }
    }
    return length;
  }, [editorState]);

  const handleBeforeInput = () => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText("").length;
    const selectedTextLength = getLengthOfSelectedText();

    if (currentContentLength - selectedTextLength > MAX_LENGTH - 1) {
      return "handled";
    }
    return "not-handled";
  };

  const handlePastedText = (pastedText) => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText("").length;
    const selectedTextLength = getLengthOfSelectedText();

    if (currentContentLength + pastedText.length - selectedTextLength > MAX_LENGTH) {
      return "handled";
    }
    return "not-handled";
  };

  return (
    <div className="text-outer-box">
      <Editor
        editorState={editorState}
        wrapperClassName="demo-wrapper"
        editorClassName="demo-editor"
        onEditorStateChange={onEditorStateChange}
        handleBeforeInput={handleBeforeInput}
        handlePastedText={handlePastedText}
        toolbar={{
          textAlign: { inDropdown: true, className: "hide" },
          inline: {
            inDropdown: false,
            options: ["bold", "italic", "underline", "strikethrough"],
          },
          list: {
            inDropdown: false,
            options: ["unordered", "ordered"],
          },
          image: { className: "hide" },
          link: {
            inDropdown: false,
            showOpenOptionOnHover: true,
            defaultTargetOption: "_self",
            options: ["link", "unlink"],
          },
          blockType: { className: "hide" },
          colorPicker: { className: "hide" },
          fontSize: { className: "hide" },
          fontFamily: { className: "hide" },
          history: { className: "hide" },
          remove: { className: "hide" },
          embedded: { className: "hide" },
        }}
      />
    </div>
  );
};

export default TextEditorQuestion;